import type { Scale } from '@tunasong/schemas'
import { isChordVariant, type Chord, type ChordVariant } from '../chord/index.js'
import ScaleLib from '../scale/scale.js'
import { hasNoLeadingTone } from './cadence.js'

export type CadenceKind = 'authentic' | 'plagal' | 'half' | 'deceptive'

export type SuppressionReason =
  /** V chord lacks a leading-tone resolution — Mixolydian, Dorian, Phrygian. */
  | 'modal-no-leading-tone'
  /** IV chord is diminished (Lydian's ♯IV°) — doesn't function as plagal predominant. */
  | 'lydian-diminished-iv'

export interface CadenceOption {
  kind: CadenceKind
  /** The proposed ending chord — what the card displays. Null when unavailable. */
  endingChord: Chord | null
  /** Penult-substitution chord (plagal: IV/iv); null for other kinds. */
  penultSubstitution: Chord | null
  /** Lexicon entry id — drives the "Why?" TheoryCard. */
  lexiconId: string
  available: boolean
  suppressionReason?: SuppressionReason
}

const LEXICON_IDS: Record<CadenceKind, string> = {
  authentic: 'cadence-authentic',
  plagal: 'cadence-plagal',
  half: 'cadence-half',
  deceptive: 'cadence-deceptive',
}

const KIND_ORDER: CadenceKind[] = ['authentic', 'plagal', 'half', 'deceptive']

const ENDING_DEGREE: Record<CadenceKind, 'I' | 'IV' | 'V' | 'VI'> = {
  authentic: 'I',
  plagal: 'I',
  half: 'V',
  deceptive: 'VI',
}

/**
 * Compute the four cadence options for a given scale. Always returns four
 * entries (one per kind), with `available: false` for kinds the scale doesn't
 * support. The caller renders disabled cards from the unavailable entries.
 *
 * Suppression rules (per Bowie's §8.Q5 verdicts):
 *   - **authentic + deceptive** suppressed when `hasNoLeadingTone(scale)` —
 *     Mixolydian, Dorian, Phrygian, modal Aeolian, Locrian. Major and
 *     natural-minor pass (minor is exempt per the harmonic-minor convention;
 *     Lydian passes because it has a natural 7). The minor-key exemption is
 *     pedagogical, not literal: writers raise the 7 on V in practice.
 *   - **plagal** suppressed when the diatonic IV is diminished (Lydian's
 *     ♯IV°). Major-IV and minor-iv both work as plagal predominants.
 *   - **half** never suppressed — landing on V at end-of-line reads as "open"
 *     even in modes where V is minor or diminished.
 *
 * Minor-key V quality: natural-minor's diatonic V is minor (Em in A minor),
 * but the cadence-lab audition wants to demonstrate the harmonic-minor V
 * resolution that writers actually hear. For `kind === 'half'` in a minor
 * KEY (not modal Aeolian) we lift the V chord to major quality. For
 * `kind === 'authentic'` the ending IS the minor i — only the (unseen)
 * dominant penult would be lifted, and v1 doesn't substitute the penult
 * for authentic (Bowie §8.Q4).
 *
 * Spec: docs/superpowers/specs/2026-05-17-cadence-lab-design.md §3.4
 * Bowie verdicts: .claude/agent-memory/bowie/project_cadence_lab_v1_scope.md
 */
export const getCadenceOptions = (scale: Scale): CadenceOption[] => {
  const modal = hasNoLeadingTone(scale)
  return KIND_ORDER.map(kind => buildOption(kind, scale, modal))
}

const buildOption = (kind: CadenceKind, scale: Scale, modal: boolean): CadenceOption => {
  const lexiconId = LEXICON_IDS[kind]

  // Authentic / deceptive require a leading-tone-bearing V (dominant function).
  if (modal && (kind === 'authentic' || kind === 'deceptive')) {
    return {
      kind,
      endingChord: null,
      penultSubstitution: null,
      lexiconId,
      available: false,
      suppressionReason: 'modal-no-leading-tone',
    }
  }

  // Plagal requires a non-diminished IV (the predominant).
  if (kind === 'plagal' && !hasPlagalIV(scale)) {
    return {
      kind,
      endingChord: null,
      penultSubstitution: null,
      lexiconId,
      available: false,
      suppressionReason: 'lydian-diminished-iv',
    }
  }

  // Half cadence in a minor KEY: lift V to major quality (harmonic-minor
  // convention). For other kinds + scale-types, the diatonic chord is used
  // as-is.
  if (kind === 'half' && scale.type === 'minor') {
    const ending = majorTriadAtDegree(scale, 'V')
    if (!ending) {
      return { kind, endingChord: null, penultSubstitution: null, lexiconId, available: false }
    }
    return { kind, endingChord: ending, penultSubstitution: null, lexiconId, available: true }
  }

  const endingChord = ScaleLib.getChordByScaleDegree(scale, ENDING_DEGREE[kind])
  if (!endingChord) {
    return { kind, endingChord: null, penultSubstitution: null, lexiconId, available: false }
  }

  // Plagal substitution: penult swaps to IV/iv. (Other kinds substitute only
  // the LAST chord — penultSubstitution stays null.) Per Bowie §8.Q4: plagal's
  // defining motion is IV→I, so the audition substitutes BOTH the penult
  // (with IV) and the last (with I).
  if (kind === 'plagal') {
    const penultSubstitution = ScaleLib.getChordByScaleDegree(scale, 'IV')
    if (!penultSubstitution) {
      return { kind, endingChord: null, penultSubstitution: null, lexiconId, available: false }
    }
    return { kind, endingChord, penultSubstitution, lexiconId, available: true }
  }

  return { kind, endingChord, penultSubstitution: null, lexiconId, available: true }
}

/** True when the scale's diatonic IV is not a diminished triad. */
const hasPlagalIV = (scale: Scale): boolean => {
  const iv = ScaleLib.getChordByScaleDegree(scale, 'IV')
  if (!iv || !isChordVariant(iv)) {
    return false
  }
  return iv.variant !== 'dim'
}

/**
 * Build a major triad on the scale's `degree`-th note. Used for minor-key
 * half cadence (harmonic-minor V lift). Returns null if the degree's note
 * can't be resolved or the chord shape isn't a ChordVariant.
 */
const majorTriadAtDegree = (scale: Scale, degree: 'V'): ChordVariant | null => {
  const naturalChord = ScaleLib.getChordByScaleDegree(scale, degree)
  if (!naturalChord || !isChordVariant(naturalChord)) {
    return null
  }
  return { root: naturalChord.root, variant: '' }
}
